import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Chip, Icon, Section, Spark, Swatch } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { mergedItem, scanStore } from "@/lib/scanStore";
import { buildTxtFile, downloadTxt, formatForPlatform, platformLink, type Platform, type PlatformDescriptions } from "@/lib/listingFormat";
import { toast } from "@/lib/toast";

const PLATFORMS: Platform[] = ["Depop", "Grailed", "eBay", "Etsy", "Poshmark", "Vinted"];

// Lightweight URL → platform name detector for the optional listing-link
// input on the final post step. Display-only; doesn't gate posting.
const LISTING_PLATFORMS: { name: string; match: RegExp }[] = [
  { name: "Depop",    match: /depop\.com/i },
  { name: "Poshmark", match: /poshmark\.com/i },
  { name: "Etsy",     match: /etsy\.com/i },
  { name: "eBay",     match: /ebay\.[a-z.]+/i },
  { name: "Vinted",   match: /vinted\.[a-z.]+/i },
];
function detectPlatform(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  return LISTING_PLATFORMS.find((p) => p.match.test(trimmed))?.name ?? null;
}

// Soft guardrail keywords for the caption field — surfaces a friendly nudge
// to run AI verification when the collector is making authenticity claims.
const AUTH_CLAIM_PATTERNS = [
  /\bauthenticated\b/i,
  /\bverified\b/i,
  /\bguaranteed\s+authentic\b/i,
  /\b100%\s*real\b/i,
];
function makesAuthClaim(text: string): boolean {
  return AUTH_CLAIM_PATTERNS.some((re) => re.test(text));
}

const DESC_CACHE_KEY = "fig_platform_descriptions";
// Per-scan key so different scans remember their own picked marketplace.
// We fall back to "anon" before a scan id exists (e.g. dev preview).
const PLATFORM_PICK_KEY = "fig.list.platform";
const platformPickKey = () =>
  `${PLATFORM_PICK_KEY}:${sessionStorage.getItem("fig_current_scan_id") || "anon"}`;

function loadPickedPlatform(): Platform | null {
  try {
    const raw = localStorage.getItem(platformPickKey());
    if (!raw) return null;
    const match = PLATFORMS.find((p) => p.toLowerCase() === raw.toLowerCase());
    return match ?? null;
  } catch {
    return null;
  }
}
const TREND_WEEKS = 12;
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

interface TrendData {
  series: number[];
  sampleCount: number;
  pctChange: number | null;
}

export default function ListForSale() {
  const nav = useNavigate();
  const { user } = useAuth();
  const item = useMemo(() => mergedItem(scanStore.getResult()), []);
  const photoUrls = useMemo(() => scanStore.getPhotos(), []);
  // Lazy initialiser — pulls the previously-picked marketplace for this scan
  // so refreshing or switching tabs doesn't drop the user's choice.
  const [platform, setPlatformRaw] = useState<Platform>(() => loadPickedPlatform() ?? "Depop");
  // Wrapper so every state change also persists. Cheap, fire-and-forget;
  // we swallow quota errors because the collector should never break on storage.
  const setPlatform = (next: Platform) => {
    setPlatformRaw(next);
    try { localStorage.setItem(platformPickKey(), next); } catch { /* quota */ }
  };
  const [price, setPrice] = useState<number>(item.pricing.suggested);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Editable tag set — seeded from the AI's auto-tags but the user can
  // add/remove freely. We dedupe + lowercase + trim so chips stay tidy.
  const [tags, setTags] = useState<string[]>(() => item.tags);
  const [tagDraft, setTagDraft] = useState("");
  const [addingTag, setAddingTag] = useState(false);
  const addTag = (raw: string) => {
    const cleaned = raw.trim().replace(/^#+/, "").replace(/\s+/g, " ").slice(0, 24);
    if (!cleaned) return;
    setTags((prev) => {
      const existing = new Set(prev.map((t) => t.toLowerCase()));
      if (existing.has(cleaned.toLowerCase())) return prev;
      return [...prev, cleaned];
    });
    setTagDraft("");
  };
  const removeTag = (t: string) =>
    setTags((prev) => prev.filter((x) => x !== t));
  const [copied, setCopied] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [descriptions, setDescriptions] = useState<PlatformDescriptions | null>(null);
  const [writing, setWriting] = useState(false);
  const [listingCity, setListingCity] = useState<string>("");
  const [autoCity, setAutoCity] = useState<string>("");
  const [editingLocation, setEditingLocation] = useState(false);
  // Pricing intel — folded in from the old /price page so the collector can
  // size up demand and comps without leaving the listing form.
  const [trend, setTrend] = useState<TrendData | null>(null);
  const [showComps, setShowComps] = useState(false);
  const [keeping, setKeeping] = useState(false);
  // Post-to-feed inline panel — toggle, caption, in-flight + posted state.
  const [caption, setCaption] = useState("");
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const CAPTION_MAX = 140;
  // Optional verification choice on the final post step. Default to
  // "share" so existing flows (and unverified posters) keep working
  // exactly as they do today. Selecting "verify" routes them through
  // the existing /result verification surface after posting.
  const [verifyChoice, setVerifyChoice] = useState<"verify" | "share">("share");
  // Optional marketplace listing URL on the final post step.
  const [listingUrl, setListingUrl] = useState("");
  // Tab nav — three focused views instead of one long scroll.
  type Tab = "price" | "market" | "feed";
  const [tab, setTab] = useState<Tab>("price");

  const p = item.pricing;
  const range = Math.max(1, p.high - p.low);

  // Keep-in-closet shortcut for collectors who decide not to list. Mirrors the
  // old /price page behavior so muscle memory still works.
  const handleKeep = async () => {
    if (keeping) return;
    setKeeping(true);
    try {
      const scanId = sessionStorage.getItem("fig_current_scan_id");
      if (!scanId) {
        toast.error("Couldn't find this scan to keep");
        setKeeping(false);
        return;
      }
      const { error } = await supabase
        .from("scans")
        .update({ posted: true, posted_to: null, status: "kept" })
        .eq("id", scanId);
      if (error) throw error;
      toast.success("Kept in your closet");
      nav("/profile/@you");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't keep this item";
      toast.error(msg);
      setKeeping(false);
    }
  };

  // Post-to-feed handler. `markKept` means flip posted=true + status='kept'
  // (the "post & mark as kept" action). Otherwise just post and stay on this
  // page so the collector can continue and add marketplaces via the primary CTA.
  const postToFeed = async (markKept: boolean) => {
    if (posting) return;
    setPosting(true);
    try {
      const scanId = sessionStorage.getItem("fig_current_scan_id");
      if (!scanId) {
        toast.error("Couldn't find this scan to post");
        setPosting(false);
        return;
      }
      const trimmed = caption.trim().slice(0, CAPTION_MAX);
      const detected = detectPlatform(listingUrl);
      const trimmedUrl = listingUrl.trim();
      const update: {
        posted: boolean;
        caption: string | null;
        status?: string;
        posted_to?: string | null;
        marketplace_links?: { name: string; url: string }[];
      } = {
        posted: true,
        caption: trimmed || null,
      };
      if (markKept) {
        update.status = "kept";
        update.posted_to = null;
      }
      // Additive: if the collector pasted a listing URL on the final step,
      // store it via the existing marketplace_links column. Doesn't touch
      // schema or any existing verified-post path.
      if (trimmedUrl) {
        update.marketplace_links = [{
          name: detected ?? "Listing",
          url: trimmedUrl,
        }];
        if (!markKept) update.posted_to = (detected ?? "other").toLowerCase();
      }
      const { error } = await supabase
        .from("scans")
        .update(update as unknown as Record<string, never>)
        .eq("id", scanId);
      if (error) throw error;
      setPosted(true);
      // Trigger grail-match recompute now that this scan is public.
      void supabase.functions.invoke("compute-grail-matches", {
        body: { kind: "scan", id: scanId },
      });
      if (markKept) {
        toast.success("Posted & kept in closet");
        nav("/profile/@you");
      } else if (verifyChoice === "verify") {
        toast.success("Posted — let's verify it");
        nav("/result");
      } else {
        toast.success("Posted to your feed");
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't post this item";
      toast.error(msg);
    } finally {
      setPosting(false);
    }
  };

  // 12-week comp trend bucket — same query the standalone Price page used.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const since = new Date(Date.now() - TREND_WEEKS * MS_PER_WEEK).toISOString();
      let q = supabase
        .from("scans")
        .select("price_suggested, created_at, category")
        .eq("posted", true)
        .not("price_suggested", "is", null)
        .gte("created_at", since);
      if (item.category) q = q.eq("category", item.category);

      const { data, error } = await q;
      if (cancelled) return;
      if (error || !data) {
        setTrend({ series: [], sampleCount: 0, pctChange: null });
        return;
      }
      const buckets: number[][] = Array.from({ length: TREND_WEEKS }, () => []);
      const now = Date.now();
      for (const row of data) {
        const priceVal = Number(row.price_suggested);
        if (!Number.isFinite(priceVal)) continue;
        const ageWeeks = Math.floor((now - new Date(row.created_at).getTime()) / MS_PER_WEEK);
        const bucket = TREND_WEEKS - 1 - ageWeeks;
        if (bucket >= 0 && bucket < TREND_WEEKS) buckets[bucket].push(priceVal);
      }
      let last = 0;
      const series = buckets.map((b) => {
        const m = median(b);
        if (m > 0) { last = m; return m; }
        return last;
      });
      const firstHalf = series.slice(0, Math.floor(TREND_WEEKS / 2)).filter((v) => v > 0);
      const secondHalf = series.slice(Math.floor(TREND_WEEKS / 2)).filter((v) => v > 0);
      const a = median(firstHalf);
      const bMed = median(secondHalf);
      const pctChange = a > 0 ? Math.round(((bMed - a) / a) * 100) : null;
      setTrend({ series, sampleCount: data.length, pctChange });
    })();
    return () => { cancelled = true; };
  }, [item.category]);

  const trendDirection = trend?.pctChange == null ? null : trend.pctChange >= 0 ? "rising" : "softening";
  const trendColor = trendDirection === "rising"
    ? "hsl(var(--olive))"
    : trendDirection === "softening"
      ? "hsl(var(--rust))"
      : "hsl(var(--ink))";

  // Load the auto-tagged city for this scan so the user can override it.
  useEffect(() => {
    const scanId = sessionStorage.getItem("fig_current_scan_id");
    if (!scanId) {
      try {
        const raw = localStorage.getItem("fig.geo.cache");
        if (raw) {
          const parsed = JSON.parse(raw) as { city: string | null };
          if (parsed?.city) { setAutoCity(parsed.city); setListingCity(parsed.city); }
        }
      } catch { /* ignore */ }
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("scans")
        .select("city")
        .eq("id", scanId)
        .maybeSingle();
      const city = data?.city ?? "";
      setAutoCity(city);
      setListingCity(city);
    })();
  }, []);

  // Load any cached AI descriptions for this scan + auto-generate once.
  useEffect(() => {
    const scanId = sessionStorage.getItem("fig_current_scan_id") || "anon";
    const raw = sessionStorage.getItem(`${DESC_CACHE_KEY}:${scanId}`);
    if (raw) {
      try { setDescriptions(JSON.parse(raw)); return; } catch { /* fall through */ }
    }
    void generateDescriptions(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateDescriptions = async (announce = true) => {
    if (writing) return;
    setWriting(true);
    try {
      const { data, error } = await supabase.functions.invoke("write-listing-descriptions", {
        body: {
          item: {
            title: item.title,
            brand: item.brand,
            era: item.era,
            origin: item.origin,
            category: item.category,
            material: item.material,
            size: item.size,
            color: item.color,
            condition: item.condition,
            conditionNotes: item.conditionNotes,
            tags,
            notes: item.notes,
            verification: item.verification ?? null,
          },
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setDescriptions(data as PlatformDescriptions);
      const scanId = sessionStorage.getItem("fig_current_scan_id") || "anon";
      try { sessionStorage.setItem(`${DESC_CACHE_KEY}:${scanId}`, JSON.stringify(data)); } catch { /* quota */ }
      if (announce) toast.success("Descriptions rewritten");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't write descriptions";
      if (announce) toast.error(msg);
      else console.warn("auto-generate descriptions failed:", msg);
    } finally {
      setWriting(false);
    }
  };

  const formatted = useMemo(
    () => formatForPlatform(platform, {
      title: item.title,
      brand: item.brand,
      era: item.era,
      origin: item.origin,
      category: item.category,
      material: item.material,
      size: item.size,
      color: item.color,
      condition: item.condition,
      conditionNotes: item.conditionNotes,
      tags,
      price,
    }, descriptions ?? undefined),
    [platform, item, price, descriptions, tags]
  );

  const aiDescriptionActive = !!descriptions?.[platform.toLowerCase() as keyof PlatformDescriptions];

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(`${key} copied`);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1400);
    } catch {
      toast.error("Couldn't copy — long-press to select instead");
    }
  };

  const markPosted = async () => {
    const scanId = sessionStorage.getItem("fig_current_scan_id");
    if (user && scanId) {
      const trimmedCity = listingCity.trim();
      const cityChanged = trimmedCity !== (autoCity || "").trim();
      const update: {
        posted: boolean;
        status: string;
        price_suggested: number;
        posted_to: string;
        marketplace_links: { name: string; url: string }[];
        city?: string | null;
        lat?: number | null;
        lng?: number | null;
      } = {
        posted: true,
        status: `Listed · ${platform}`,
        price_suggested: price,
        posted_to: platform.toLowerCase(),
        // Seed the multi-marketplace list with this first platform (URL blank
        // — the collector pastes the live link from Result/Feed once they have it).
        marketplace_links: [{ name: platform, url: "" }],
      };
      if (cityChanged) {
        update.city = trimmedCity || null;
        update.lat = null;
        update.lng = null;
      }
      // Cast through unknown — the auto-generated scans Update type regenerates
      // after migration approval; until then it doesn't know `marketplace_links`.
      await supabase
        .from("scans")
        .update(update as unknown as Record<string, never>)
        .eq("id", scanId)
        .eq("user_id", user.id);
    }
    sessionStorage.setItem("fig_last_post_platform", platform);
    sessionStorage.setItem("fig_last_post_price", String(price));
  };

  const openPlatform = async () => {
    setOpening(true);
    const link = platformLink(platform);
    await markPosted();
    window.open(link.web, "_blank", "noopener,noreferrer");
    setOpening(false);
    setSheetOpen(false);
    nav("/posted");
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 40 }}>
        {/* ─── Top bar ─────────────────────────────────────────── */}
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Price &amp; list</span>
          <div style={{ width: 30 }} />
        </div>

        {/* ─── TAB BAR — three focused views: price intel, marketplace pick, feed post.
            Underline indicator inherits the active rust accent so theme picks shine through. */}
        <div style={{ padding: "18px 20px 0" }}>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
            borderBottom: "1px solid hsl(var(--ink))",
          }}>
            {([
              { id: "price",  label: "Price"        },
              { id: "market", label: "Marketplaces" },
              { id: "feed",   label: "Feed"         },
            ] as { id: Tab; label: string }[]).map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  style={{
                    background: "transparent", border: "none", cursor: "pointer",
                    padding: "10px 0 12px", position: "relative",
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontWeight: active ? 800 : 600,
                    fontSize: 15,
                    color: active ? "hsl(var(--ink))" : "hsl(var(--ink-2))",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {t.label}
                  {active && (
                    <span style={{
                      position: "absolute", left: "20%", right: "20%", bottom: -1,
                      height: 3, background: "hsl(var(--rust))",
                    }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {tab === "price" && <>
        {/* ─── PRICE TAB — folded in from the old /price page so the
            collector sees demand + range + comps before they pick a number.  */}
        <Section pad={22} eyebrow="Market" title={`$${p.suggested} suggested`}>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 13, color: "hsl(var(--ink-2))", marginTop: -6,
          }}>
            Range ${p.low}–${p.high} · {p.suggestedConfidence} confidence
          </div>

          {/* Range bar — sits right under the suggested price so the collector
              can read low/sug/high at a glance before considering the trend. */}
          <div style={{ marginTop: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
              <span className="caps" style={{ fontSize: 12 }}>Low ${p.low}</span>
              <span className="caps" style={{ color: "hsl(var(--rust))", fontSize: 12 }}>Sug ${p.suggested}</span>
              <span className="caps" style={{ fontSize: 12 }}>High ${p.high}</span>
            </div>
            <div style={{
              position: "relative", height: 24,
              background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))",
            }}>
              <div style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(90deg, hsl(var(--paper-3)), hsl(var(--sand)), hsl(var(--rust-soft)))",
                opacity: 0.5,
              }} />
              <div style={{
                position: "absolute", top: -4, bottom: -4,
                left: `${((p.suggested - p.low) / range) * 100}%`,
                width: 3, background: "hsl(var(--rust))", transform: "translateX(-1.5px)",
              }} />
            </div>
          </div>

          {/* Trend block — its own card under the range bar. */}
          <Card accent={trendColor} style={{ padding: "12px 14px", marginTop: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900,
                fontSize: 28, color: "hsl(var(--ink))", letterSpacing: "-0.02em", lineHeight: 0.95,
              }}>
                {trend === null
                  ? "…"
                  : trend.pctChange === null
                    ? "—"
                    : <>{trend.pctChange >= 0 ? "+" : ""}{trend.pctChange}%</>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="caps" style={{ color: "hsl(var(--rust))" }}>12-week trend</div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700,
                  fontSize: 13, color: "hsl(var(--ink))", lineHeight: 1.1, marginTop: 2,
                }}>
                  {trend === null
                    ? "Loading market…"
                    : trend.sampleCount === 0
                      ? "No comparables yet"
                      : trend.pctChange === null
                        ? "Building baseline"
                        : `Demand ${trendDirection}`}
                </div>
                <div className="caps" style={{ marginTop: 2, color: "hsl(var(--ink-2))" }}>
                  {trend?.sampleCount ?? 0} comp {trend?.sampleCount === 1 ? "listing" : "listings"} · last 12 wks
                </div>
              </div>
              {trend && trend.series.some((v) => v > 0) && (
                <Spark data={trend.series} color={trendColor} fill={trendColor} width={88} height={36} />
              )}
            </div>
          </Card>

          {/* Collapsible comps + platform breakdown — secondary detail */}
          <button
            onClick={() => setShowComps((s) => !s)}
            style={{
              width: "100%", marginTop: 10,
              border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))",
              padding: "10px 14px", cursor: "pointer", display: "flex",
              justifyContent: "space-between", alignItems: "center",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: "0.12em",
              textTransform: "uppercase", color: "hsl(var(--ink))",
            }}
          >
            <span>Comps &amp; platform fees</span>
            {Icon.chev(14, showComps ? "u" : "d")}
          </button>
          {showComps && (
            <div style={{ marginTop: 10 }}>
              <div className="no-scrollbar" style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, marginRight: -20, paddingRight: 20 }}>
                {p.comps.slice(0, 6).map((c, i) => (
                  <Card key={i} style={{ flexShrink: 0, width: 120 }}>
                    <Swatch kind={c.img} style={{ width: "100%", aspectRatio: "1/1", borderBottom: "1px solid hsl(var(--ink))" }} />
                    <div style={{ padding: "6px 8px 8px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span className="caps" style={{ color: "hsl(var(--rust))", fontSize: 9 }}>{c.platform}</span>
                        <span className="caps" style={{ fontSize: 9 }}>{c.days}d</span>
                      </div>
                      <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 16, color: "hsl(var(--ink))", marginTop: 4 }}>
                        ${c.price}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
                {p.platforms.map((pl) => (
                  <div key={pl.name} style={{
                    border: "1px solid hsl(var(--sand-deep))",
                    padding: "8px 12px",
                    display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    background: pl.hot ? "hsl(var(--rust-soft) / 0.3)" : "transparent",
                  }}>
                    <div>
                      <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14, color: "hsl(var(--ink))" }}>
                        {pl.name}
                      </span>
                      {pl.hot && <span className="caps" style={{ marginLeft: 6, color: "hsl(var(--rust))", fontSize: 9 }}>best match</span>}
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                      <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>−{pl.fee}%</span>
                      <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 14, color: "hsl(var(--ink))" }}>
                        net ${pl.net}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* ─── 4. PRICE — big editable hero ────────────────────── */}
        <Section pad={22} eyebrow="List price" meta={
          <span className="caps" style={{ color: "hsl(var(--rust))" }}>suggested ${item.pricing.suggested}</span>
        }>
          <Card style={{ padding: "16px 18px", display: "flex", alignItems: "baseline", gap: 10 }}>
            <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 44, color: "hsl(var(--ink))" }}>$</span>
            <input
              type="number" value={price}
              onChange={(e) => setPrice(+e.target.value)}
              style={{
                background: "transparent", border: "none", outline: "none",
                fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 44,
                color: "hsl(var(--ink))", width: 120,
              }}
            />
            <div style={{ flex: 1, textAlign: "right" }}>
              {(() => {
                // Pull the fee for the currently-selected marketplace from
                // the pricing intel block (p.platforms). Falls back to 10%
                // if this platform isn't in the comp set yet.
                const pl = p.platforms.find(
                  (x) => x.name.toLowerCase() === platform.toLowerCase()
                );
                const feePct = pl?.fee ?? 10;
                const keep = Math.round(price * (1 - feePct / 100));
                return (
                  <>
                    <div className="caps">On {platform}</div>
                    <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 18, color: "hsl(var(--ink))" }}>
                      ~${keep}
                    </div>
                    <div className="caps" style={{ color: "hsl(var(--ink-2))", marginTop: 2 }}>
                      after ~{feePct}% fees
                    </div>
                  </>
                );
              })()}
            </div>
          </Card>
        </Section>

        {/* ─── 5. AUTO-TAGS ────────────────────────────────────── */}
        <Section pad={22} eyebrow="Auto-tags">
          {/* Tags are editable — each chip shows a × to remove, and the
              "+ add tag" chip swaps to an inline input on click. Tags drive
              the listing copy (formatForPlatform), so changes here ripple
              through every marketplace preview. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            {tags.map((t) => (
              <span key={t} style={{ display: "inline-flex", alignItems: "stretch" }}>
                <Chip>#{t}</Chip>
                <button
                  type="button"
                  aria-label={`Remove tag ${t}`}
                  onClick={() => removeTag(t)}
                  style={{
                    border: "1px solid hsl(var(--ink))",
                    borderLeft: "none",
                    background: "hsl(var(--cream))",
                    color: "hsl(var(--ink))",
                    padding: "0 6px",
                    cursor: "pointer",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11, lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
            {addingTag ? (
              <input
                autoFocus
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value.slice(0, 24))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addTag(tagDraft); setAddingTag(false); }
                  else if (e.key === "Escape") { setTagDraft(""); setAddingTag(false); }
                }}
                onBlur={() => { if (tagDraft.trim()) addTag(tagDraft); setAddingTag(false); }}
                placeholder="new tag"
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
                  padding: "3px 7px",
                  border: "1px solid hsl(var(--ink))",
                  background: "hsl(var(--cream))",
                  color: "hsl(var(--ink))",
                  outline: "none",
                  width: 110,
                }}
              />
            ) : (
              <button
                type="button"
                onClick={() => setAddingTag(true)}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
                  padding: "3px 7px",
                  border: "1px solid hsl(var(--ink))",
                  background: "hsl(var(--ink))",
                  color: "hsl(var(--cream))",
                  cursor: "pointer",
                }}
              >
                + add tag
              </button>
            )}
          </div>
        </Section>
        </>}

        {tab === "market" && <>
        {/* ─── MARKETPLACES TAB — recommended first, then a tidy list.
            Tapping a row both selects it AND opens the copy sheet for that
            marketplace, since picking is the whole point of being here. */}
        <Section pad={22} eyebrow="Best fit" title="Where this sells">
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 13, color: "hsl(var(--ink-2))", marginTop: -6, marginBottom: 12,
          }}>
            Tap a marketplace to prep the listing. Take-home is your cut after fees.
          </div>

          {(() => {
            // Build a ranked entry for EVERY supported marketplace, even ones
            // Fig's pricing intel didn't return — so the dropdown is always
            // the full set. Missing entries fall back to a 10% fee and a
            // synthetic net so they sort below ones we have real data for.
            const FALLBACK_FEE = 10;
            type Ranked = { name: string; fee: number; net: number; hot: boolean };
            const ranked: Ranked[] = PLATFORMS.map((name) => {
              const known = p.platforms.find(
                (x) => x.name.toLowerCase() === name.toLowerCase(),
              );
              if (known) {
                return { name: known.name, fee: known.fee, net: known.net, hot: !!known.hot };
              }
              return {
                name,
                fee: FALLBACK_FEE,
                // Synthetic net based on current price so ordering still makes
                // sense, but slightly penalised so real-data marketplaces win
                // ties.
                net: Math.round(price * (1 - FALLBACK_FEE / 100)) - 1,
                hot: false,
              };
            }).sort((a, b) => {
              if (a.hot !== b.hot) return a.hot ? -1 : 1;
              return b.net - a.net;
            });
            const recommended = ranked.filter((x) => x.hot).slice(0, 2);
            // Dropdown = every marketplace NOT already surfaced as a
            // recommended card — keeps Fig's full collector available without
            // duplicating the best-match entries above.
            const others = ranked.filter((x) => !recommended.includes(x));

            const pickAndOpen = (name: string) => {
              const match = PLATFORMS.find((pl) => pl.toLowerCase() === name.toLowerCase());
              if (match) setPlatform(match);
              setSheetOpen(true);
            };

            // ── Shared rationale builders ──
            // Audience fit is platform-intrinsic; same blurb wherever a
            // marketplace appears. Rank-aware lines (take-home, position) are
            // computed per row so the recommended card and the "Other
            // marketplaces" row each get an honest reason.
            const audienceBlurb: Record<string, string> = {
              depop:    "Depop's Gen-Z streetwear crowd hunts this aesthetic.",
              grailed:  "Grailed collectors pay premiums for this brand & era.",
              ebay:     "eBay's wide search audience moves this category fastest.",
              etsy:     "Etsy vintage curators love this era and story.",
              poshmark: "Poshmark style buyers target this brand and silhouette.",
              vinted:   "Vinted's casual EU/UK shoppers grab this category quickly.",
            };
            const audienceFor = (name: string) =>
              audienceBlurb[name.toLowerCase()] ?? `${name} buyers actively shop this category.`;

            // ── Item-aware marketplace analysis ──
            // 2–3 sentence breakdown that reacts to THIS specific scan
            // (brand, era, category) plus the live demand trend. Intentionally
            // omits fees / take-home numbers — those already live in the
            // equation column on the right of each row.
            const brandTxt = (item.brand ?? "").trim();
            const eraTxt   = (item.era   ?? "").trim();
            const catTxt   = (item.category ?? "").trim().toLowerCase();
            const itemNoun = catTxt || "piece";
            const brandEra = [brandTxt, eraTxt].filter(Boolean).join(" ");
            const trendClause =
              trend && trend.sampleCount > 0 && trend.pctChange !== null
                ? trend.pctChange >= 5
                  ? ` Demand for ${itemNoun} is climbing (${trend.pctChange}% over 12 weeks), which plays to its strengths.`
                  : trend.pctChange <= -5
                    ? ` Category demand is softening (${trend.pctChange}%), so listings here may sit a little longer.`
                    : ""
                : "";

            const analysisFor = (name: string): string => {
              const subject = brandEra || itemNoun;
              switch (name.toLowerCase()) {
                case "depop":
                  return `Depop indexes hard on Gen-Z resellers chasing styled, photo-led drops — ${subject} fits that aesthetic feed.${trendClause} Expect quick saves and DMs from buyers who already know the look they want.`;
                case "grailed":
                  return `Grailed is a focused menswear & archive crowd that researches before they buy, so ${subject} lands in front of collectors who recognise the era.${trendClause} Listings here tend to fetch stronger asks when the brand has provenance.`;
                case "ebay":
                  return `eBay's search-driven audience is the widest of any platform, which surfaces ${subject} to buyers actively typing the brand and size.${trendClause} Best when you want volume of eyeballs over a curated feed.`;
                case "etsy":
                  return `Etsy's vintage shoppers come for story and styling, and ${subject} reads as exactly the kind of curated find they save.${trendClause} Strong fit when the piece has age, character, or a narrative to lean into.`;
                case "poshmark":
                  return `Poshmark skews towards style-led closet shoppers in the US who follow brands and silhouettes, putting ${subject} in front of repeat buyers.${trendClause} Social sharing tools also give listings extra reach inside the app.`;
                case "vinted":
                  return `Vinted's casual EU/UK buyers prioritise easy, affordable second-hand — ${subject} slots cleanly into that browsing pattern.${trendClause} Listings move fastest when priced approachably for everyday wear.`;
                default:
                  return `${name} buyers actively shop ${itemNoun}, which gives ${subject} a credible audience here.${trendClause}`;
              }
            };

            // Single source of truth for rank wording — uses the SAME ordered
            // list that drives the visual layout (hot first, then by net),
            // with dense ranking so ties (same hot flag AND same net) share
            // a position. That keeps the recommended card and the matching
            // "Other marketplaces" row honest about each other.
            const rankByName = new Map<string, number>();
            {
              let lastRank = 0;
              let lastKey = "";
              ranked.forEach((entry, i) => {
                const key = `${entry.hot ? 1 : 0}|${entry.net}`;
                if (key !== lastKey) {
                  lastRank = i + 1;
                  lastKey = key;
                }
                rankByName.set(entry.name, lastRank);
              });
            }
            const rankFor = (name: string) => rankByName.get(name) ?? 0;
            const ordinalRank = (r: number) =>
              r === 1 ? "Top pick"
                : r === 2 ? "2nd best"
                : r === 3 ? "3rd best"
                : `#${r} pick`;

            const demandLine =
              trend && trend.sampleCount > 0 && trend.pctChange !== null
                ? trend.pctChange >= 5
                  ? `Demand for this category is up ${trend.pctChange}% over 12 weeks.`
                  : trend.pctChange <= -5
                    ? `Demand is softening (${trend.pctChange}%) — list soon to catch buyers.`
                    : "Demand is steady across the last 12 weeks."
                : null;

            // Explain why a marketplace sits LOWER than the top of the list.
            // We compare against the #1 ranked entry and pick the dominant
            // reason: weaker audience match (not flagged hot), thinner
            // take-home, or no real demand data (synthetic fallback).
            const top = ranked[0];
            const knownNames = new Set(
              p.platforms.map((x) => x.name.toLowerCase()),
            );
            const whyLowerFor = (entry: Ranked): string | null => {
              if (!top || entry.name === top.name) return null;
              const isFallback = !knownNames.has(entry.name.toLowerCase());
              if (isFallback) {
                return `Ranked lower — Fig has no demand signal for ${entry.name} on this piece yet.`;
              }
              if (top.hot && !entry.hot) {
                return `Ranked lower — weaker audience match than ${top.name} for this brand & era.`;
              }
              const gap = top.net - entry.net;
              if (gap > 0) {
                return `Ranked lower — ~$${gap} less take-home than ${top.name} after fees.`;
              }
              return `Ranked lower — ${top.name} edges it out on overall fit.`;
            };

            // Reason line for the compact "Other marketplaces" rows.
            // Rank position is now shown as a number marker in the top-left
            // corner of each row, so the analysis copy stays focused on
            // WHY the marketplace fits this item.
            const otherReasonFor = (name: string) => analysisFor(name);

            return (
              <>
                {recommended.length > 0 && (
                  <div style={{ display: "grid", gap: 10, marginBottom: 14 }}>
                    {recommended.map((pl) => {
                      const active = platform.toLowerCase() === pl.name.toLowerCase();
                      const myKeep = Math.round(price * (1 - pl.fee / 100));
                      const audienceLine = audienceFor(pl.name);
                      // Use the unified rank wording so this card matches
                      // the corresponding "Other marketplaces" row exactly.
                      // Take-home / fee numbers are shown in the price
                      // column above, so we don't repeat them in the reason.
                      const overallRank = rankFor(pl.name);
                      const rankLine = `${ordinalRank(overallRank)} overall for this piece.`;
                      const lowerLine = whyLowerFor(pl);
                      return (
                        <button key={pl.name} onClick={() => pickAndOpen(pl.name)} style={{
                          textAlign: "left", cursor: "pointer",
                          padding: "16px 18px 16px 52px",
                          border: "1.5px solid hsl(var(--ink))",
                          background: active ? "hsl(var(--rust-soft) / 0.4)" : "hsl(var(--cream))",
                          position: "relative",
                        }}>
                          {/* Rank marker — top-left, mirrors the same number
                              shown on the Other marketplaces rows below. */}
                          <div style={{
                            position: "absolute", top: 12, left: 12,
                            width: 28, height: 28,
                            border: "1.5px solid hsl(var(--ink))",
                            background: "hsl(var(--cream))", color: "hsl(var(--ink))",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 14,
                          }}>
                            {overallRank}
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                            <div>
                              <div className="caps" style={{ color: "hsl(var(--rust))" }}>Best match</div>
                              <div style={{
                                fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800,
                                fontSize: 24, color: "hsl(var(--ink))", letterSpacing: "-0.01em",
                                marginTop: 2,
                              }}>
                                {pl.name}
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              {/* Money-flow equation: list price → fees → take-home.
                                  Stacking it vertically with shrinking weights makes
                                  the math read at a glance. */}
                              <div style={{
                                fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700,
                                fontSize: 18, color: "hsl(var(--ink))",
                                display: "flex", justifyContent: "space-between",
                                alignItems: "baseline", gap: 8,
                              }}>
                                <span className="caps" style={{ fontSize: 10, color: "hsl(var(--ink-2))", fontWeight: 400 }}>list price</span>
                                <span>${price}</span>
                              </div>
                              <div style={{
                                fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 600,
                                fontSize: 13, color: "hsl(var(--ink-2))", marginTop: 4,
                                display: "flex", justifyContent: "space-between",
                                alignItems: "baseline", gap: 8,
                              }}>
                                <span className="caps" style={{ fontSize: 10, fontWeight: 400 }}>fees</span>
                                <span>− {pl.fee}%</span>
                              </div>
                              <div style={{
                                fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800,
                                fontSize: 22, color: "hsl(var(--ink))",
                                marginTop: 4, paddingTop: 4,
                                borderTop: "1px solid hsl(var(--ink))",
                                display: "flex", justifyContent: "flex-end",
                                alignItems: "baseline", gap: 8,
                              }}>
                                <span className="caps" style={{ fontSize: 10, color: "hsl(var(--rust))", fontWeight: 400 }}>take-home</span>
                                <span>${myKeep}</span>
                              </div>
                            </div>
                          </div>
                          {/* ── Why Fig recommended this card ── */}
                          <div style={{
                            marginTop: 12, paddingTop: 10,
                            borderTop: "1px dashed hsl(var(--ink-2))",
                          }}>
                            <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 6 }}>
                              Why Fig picked this
                            </div>
                            {/* Item-aware analysis paragraph (same engine that
                                drives the Other marketplace rows) — gives the
                                user the full reasoning, not just one-liners. */}
                            <p style={{
                              margin: 0,
                              fontFamily: "'Fraunces', 'Playfair Display', serif",
                              fontSize: 12.5, color: "hsl(var(--ink))", lineHeight: 1.5,
                            }}>
                              {analysisFor(pl.name)}
                            </p>
                            <ul style={{
                              margin: "8px 0 0", paddingLeft: 16,
                              fontFamily: "'Fraunces', 'Playfair Display', serif",
                              fontSize: 12.5, color: "hsl(var(--ink))", lineHeight: 1.45,
                            }}>
                              {demandLine && <li>{demandLine}</li>}
                              {lowerLine && <li>{lowerLine}</li>}
                            </ul>
                          </div>
                          <div style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            marginTop: 12, paddingTop: 10, borderTop: "1px dashed hsl(var(--ink-2))",
                          }}>
                            <span style={{
                              fontFamily: "'Fraunces', 'Playfair Display', serif",
                              fontSize: 12, color: "hsl(var(--ink-2))",
                            }}>
                              Prep listing for {pl.name}
                            </span>
                            <span className="caps" style={{ color: "hsl(var(--rust))" }}>
                              Open →
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {others.length > 0 && (
                  <>
                    <div className="caps" style={{ color: "hsl(var(--ink-2))", marginBottom: 8 }}>
                      Other marketplaces
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {others.map((pl) => {
                        const active = platform.toLowerCase() === pl.name.toLowerCase();
                        const myKeep = Math.round(price * (1 - pl.fee / 100));
                        const reason = otherReasonFor(pl.name);
                        const lowerLine = whyLowerFor(pl);
                        const rowRank = rankFor(pl.name);
                        return (
                          <button key={pl.name} onClick={() => pickAndOpen(pl.name)} style={{
                            textAlign: "left", cursor: "pointer",
                            border: "1px solid hsl(var(--ink))",
                            background: active ? "hsl(var(--ink))" : "transparent",
                            color: active ? "hsl(var(--cream))" : "hsl(var(--ink))",
                            padding: "10px 14px 10px 44px",
                            display: "block",
                            position: "relative",
                          }}>
                            {/* Rank marker — same numbering used on the
                                recommended cards above. */}
                            <div style={{
                              position: "absolute", top: 8, left: 8,
                              width: 26, height: 26,
                              border: `1px solid ${active ? "hsl(var(--cream))" : "hsl(var(--ink))"}`,
                              background: active ? "hsl(var(--ink))" : "hsl(var(--cream))",
                              color: active ? "hsl(var(--cream))" : "hsl(var(--ink))",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 13,
                            }}>
                              {rowRank}
                            </div>
                            <div style={{
                              display: "flex", justifyContent: "space-between", alignItems: "baseline",
                            }}>
                              <span style={{
                                fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 15,
                              }}>
                                {pl.name}
                              </span>
                              <span style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                                {/* Compact one-line equation: list → fees → take-home,
                                    mirroring the recommended card stack but condensed. */}
                                <span style={{
                                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                                  fontSize: 12, lineHeight: 1.2,
                                  color: active ? "hsl(var(--cream))" : "hsl(var(--ink-2))",
                                  whiteSpace: "nowrap",
                                }}>
                                  ${price}
                                  <span style={{ margin: "0 4px" }}>−</span>
                                  {pl.fee}%
                                  <span style={{ margin: "0 4px" }}>=</span>
                                  <span style={{
                                    fontWeight: 700, fontSize: 14,
                                    color: active ? "hsl(var(--cream))" : "hsl(var(--ink))",
                                  }}>
                                    ${myKeep}
                                  </span>
                                </span>
                              </span>
                            </div>
                            <div style={{
                              fontFamily: "'Fraunces', 'Playfair Display', serif",
                              fontSize: 11.5, lineHeight: 1.35, marginTop: 4,
                              color: active ? "hsl(var(--cream))" : "hsl(var(--ink-2))",
                            }}>
                              {reason}
                            </div>
                            {lowerLine && (
                              <div style={{
                                fontFamily: "'Fraunces', 'Playfair Display', serif",
                                fontSize: 11.5, lineHeight: 1.35, marginTop: 2,
                                color: active ? "hsl(var(--cream))" : "hsl(var(--ink-2))",
                              }}>
                                {lowerLine}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </Section>

        {/* Location + certificate live alongside marketplace pick — they both
            shape what shoppers see when the listing goes live. */}

        {/* ─── 6. LOCATION ─────────────────────────────────────── */}
        <Section pad={22} eyebrow="Item location" meta={
          !editingLocation ? (
            <button
              onClick={() => setEditingLocation(true)}
              style={{
                background: "transparent", border: "none", padding: 0, cursor: "pointer",
                color: "hsl(var(--rust))", fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
              }}
            >
              {listingCity ? "Override" : "Set location"}
            </button>
          ) : null
        }>
          {editingLocation ? (
            <Card style={{ padding: "10px 12px" }}>
              <input
                type="text"
                value={listingCity}
                onChange={(e) => setListingCity(e.target.value)}
                placeholder="e.g. Brooklyn, NY"
                autoFocus
                style={{
                  width: "100%", background: "transparent", border: "none", outline: "none",
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 18,
                  color: "hsl(var(--ink))",
                }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, gap: 8 }}>
                <span style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 11,
                  color: "hsl(var(--ink-2))",
                }}>
                  {autoCity ? `Auto-tagged ${autoCity}.` : "Where shoppers see the item is."}
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  {autoCity && listingCity.trim() !== autoCity.trim() && (
                    <button
                      onClick={() => setListingCity(autoCity)}
                      style={{
                        background: "transparent", color: "hsl(var(--ink))",
                        border: "1px solid hsl(var(--ink))",
                        padding: "4px 8px",
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                        letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer",
                      }}
                    >
                      Reset
                    </button>
                  )}
                  <button
                    onClick={() => setEditingLocation(false)}
                    style={{
                      background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                      border: "none", padding: "4px 10px",
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                      letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer",
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </Card>
          ) : (
            <Card style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ color: "hsl(var(--ink))", fontSize: 16 }}>📍</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 16, color: "hsl(var(--ink))", lineHeight: 1.1 }}>
                  {listingCity || "No location set"}
                </div>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 11, color: "hsl(var(--ink-2))", marginTop: 2 }}>
                  {listingCity && autoCity && listingCity.trim() !== autoCity.trim()
                    ? `Overridden — auto-tag was ${autoCity}`
                    : listingCity
                      ? "Auto-tagged from your current location"
                      : "Tap to set so shoppers can find it on Nearby"}
                </div>
              </div>
            </Card>
          )}
        </Section>

        {/* ─── 7. FIG CERTIFICATE TOGGLE ───────────────────────── */}
        <Section pad={22}>
          {item.verification && (() => {
            const v = item.verification;
            const tone =
              v.tier === "photo_verified" ? "hsl(var(--olive))" :
              v.tier === "uncertain"      ? "hsl(var(--ink))"   :
                                            "hsl(var(--rust))";
            const tierLabel =
              v.tier === "photo_verified" ? "Photo-verified" :
              v.tier === "uncertain"      ? "Sold as honestly unsure" :
                                            "Self-reported";
            const subline =
              v.tier === "photo_verified" ? `Listing copy will state: "${v.category_flag}, verified by Fig."`
              : v.tier === "uncertain"    ? `Listing will say "material unverified — sold as-is" so shoppers know upfront.`
              :                             `Listing copy will hedge — Fig won't claim "${v.category_flag}" as fact.`;
            return (
              <Card accent={tone} style={{ padding: "12px 14px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                  <div className="caps" style={{ color: tone }}>{v.category_flag} · {tierLabel}</div>
                  <button
                    onClick={() => nav("/result")}
                    style={{
                      background: "transparent", border: "none", padding: 0, cursor: "pointer",
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: "0.08em",
                      textTransform: "uppercase", color: "hsl(var(--ink-2))",
                    }}
                  >
                    Change ↗
                  </button>
                </div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 12, color: "hsl(var(--ink-2))", lineHeight: 1.35, marginTop: 4,
                }}>
                  {subline}
                </div>
              </Card>
            );
          })()}
        </Section>
        </>}

        {tab === "feed" && <>
        {/* ─── FEED TAB — final share-to-fig step. Caption + post, with a
            quiet "keep in closet" escape hatch for items you're not posting. */}
        <Section pad={22} eyebrow="Share to fig" title={posted ? "Posted ✓" : "Tell your feed"}>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 13, color: "hsl(var(--ink-2))", marginTop: -6, marginBottom: 14,
          }}>
            {posted
              ? "This scan is live in your feed. Collectors who follow you will see it."
              : "Posts go to followers and surface on Nearby. Skip if you're keeping it private."}
          </div>

          {!posted && (
            <Card style={{ padding: "14px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                <div className="caps" style={{ color: "hsl(var(--rust))" }}>
                  Caption <span style={{ color: "hsl(var(--ink-2))" }}>(optional)</span>
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                  color: "hsl(var(--ink-2))", letterSpacing: "0.06em",
                }}>
                  {caption.length}/{CAPTION_MAX}
                </div>
              </div>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_MAX))}
                disabled={posting}
                placeholder="Say something about this find…"
                rows={3}
                style={{
                  width: "100%", boxSizing: "border-box", padding: "10px 12px",
                  border: "1px dashed hsl(var(--ink))",
                  background: "hsl(var(--paper))", color: "hsl(var(--ink))",
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 15, lineHeight: 1.35, resize: "vertical", minHeight: 70, outline: "none",
                }}
              />
              {makesAuthClaim(caption) && verifyChoice !== "verify" && (
                <div style={{
                  marginTop: 8,
                  color: "hsl(var(--marigold))",
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 12, lineHeight: 1.35,
                }}>
                  Add FIG verification to back this up — it's free and takes under a minute.
                </div>
              )}
            </Card>
          )}

          {!posted && (
            <>
              {/* ── Verification choice (additive, defaults to share) ── */}
              <div className="caps" style={{ color: "hsl(var(--rust))", marginTop: 18, marginBottom: 8 }}>
                Verification
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {([
                  {
                    key: "verify" as const,
                    label: "FIG Verified",
                    sub: "Run your item through FIG authentication. Unlocks the FIG verified badge and authenticity score on your post.",
                    icon: "🛡",
                  },
                  {
                    key: "share" as const,
                    label: "Share as find",
                    sub: "Post your find to the community. You can always verify later from your post.",
                    icon: "✓",
                  },
                ]).map((opt) => {
                  const selected = verifyChoice === opt.key;
                  const isVerify = opt.key === "verify";
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setVerifyChoice(opt.key)}
                      style={{
                        textAlign: "left",
                        padding: "12px 14px",
                        cursor: "pointer",
                        border: selected
                          ? `1px solid ${isVerify ? "hsl(var(--marigold))" : "hsl(var(--ink))"}`
                          : "1px solid hsl(var(--ink-2) / 0.3)",
                        background: selected
                          ? (isVerify ? "hsl(var(--marigold) / 0.12)" : "hsl(var(--cream))")
                          : "hsl(var(--paper))",
                        display: "flex", gap: 10, alignItems: "flex-start",
                      }}
                    >
                      <div style={{
                        width: 22, height: 22, flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: isVerify ? "hsl(var(--marigold))" : (selected ? "hsl(var(--ink))" : "transparent"),
                        // Text on a teal/marigold disc must be cream — never berry.
                        color: isVerify ? "hsl(var(--marigold-fg))" : (selected ? "hsl(var(--cream))" : "hsl(var(--ink-2))"),
                        border: isVerify ? "1px solid hsl(var(--marigold))" : "1px solid hsl(var(--ink-2))",
                        fontSize: 12, fontWeight: 700,
                      }}>{opt.icon}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700,
                          fontSize: 15, color: "hsl(var(--ink))",
                        }}>{opt.label}</div>
                        <div style={{
                          fontFamily: "'Fraunces', 'Playfair Display', serif",
                          fontSize: 12, color: "hsl(var(--ink-2))", marginTop: 2, lineHeight: 1.4,
                        }}>{opt.sub}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* ── Optional listing URL with platform pills ── */}
              <div className="caps" style={{ color: "hsl(var(--rust))", marginTop: 18, marginBottom: 6 }}>
                Where's it listed? <span style={{ color: "hsl(var(--ink-2))" }}>(optional)</span>
              </div>
              <input
                type="url"
                value={listingUrl}
                onChange={(e) => setListingUrl(e.target.value)}
                disabled={posting}
                placeholder="Paste your Depop, Poshmark, Etsy, or other listing link"
                style={{
                  width: "100%", boxSizing: "border-box", padding: "10px 12px",
                  border: "1px dashed hsl(var(--ink))",
                  background: "hsl(var(--paper))", color: "hsl(var(--ink))",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, outline: "none",
                }}
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {LISTING_PLATFORMS.map((p) => {
                  const active = detectPlatform(listingUrl) === p.name;
                  return (
                    <span key={p.name} style={{
                      padding: "3px 8px",
                      border: `1px solid ${active ? "hsl(var(--ink))" : "hsl(var(--ink-2) / 0.3)"}`,
                      background: active ? "hsl(var(--ink))" : "transparent",
                      color: active ? "hsl(var(--cream))" : "hsl(var(--ink-2))",
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                    }}>{p.name}</span>
                  );
                })}
              </div>
            </>
          )}
        </Section>

        <Section pad={22}>
          {!posted ? (
            <>
              <button
                onClick={() => postToFeed(false)}
                disabled={posting}
                className="btn btn-rust"
                style={{
                  width: "100%", padding: "18px", fontSize: 16,
                  cursor: posting ? "wait" : "pointer", opacity: posting ? 0.7 : 1,
                }}
              >
                {posting
                  ? (verifyChoice === "verify" ? "Posting & verifying…" : "Posting…")
                  : (verifyChoice === "verify" ? "Post & verify with FIG →" : "Post to feed →")}
              </button>
              <button
                onClick={() => postToFeed(true)}
                disabled={posting}
                style={{
                  width: "100%", padding: "14px", marginTop: 10,
                  background: "transparent", color: "hsl(var(--ink))",
                  border: "1.5px solid hsl(var(--ink))",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                  letterSpacing: "0.1em", textTransform: "uppercase",
                  cursor: posting ? "wait" : "pointer",
                }}
              >
                Post &amp; keep in closet
              </button>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 12, color: "hsl(var(--ink-2))", textAlign: "center", marginTop: 10,
              }}>
                Already listing it? Hop back to <button
                  onClick={() => setTab("market")}
                  style={{
                    background: "none", border: "none", padding: 0, cursor: "pointer",
                    color: "hsl(var(--rust))", textDecoration: "underline",
                    fontFamily: "inherit", fontStyle: "inherit", fontSize: "inherit",
                  }}
                >Marketplaces</button> first.
              </div>
            </>
          ) : (
            <button
              onClick={() => nav("/profile/@you")}
              className="btn btn-rust"
              style={{ width: "100%", padding: "18px", fontSize: 16 }}
            >
              View on your profile →
            </button>
          )}

          {/* Quiet escape: not posting, not listing, just keep it. */}
          <button
            onClick={handleKeep}
            disabled={keeping || posting}
            style={{
              width: "100%", padding: "12px", marginTop: 18,
              background: "transparent", color: "hsl(var(--ink-2))",
              border: "1px dashed hsl(var(--ink-2))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.1em", textTransform: "uppercase",
              cursor: keeping ? "wait" : "pointer", opacity: keeping ? 0.6 : 1,
            }}
          >
            {keeping ? "Keeping…" : "Skip — just keep in closet"}
          </button>
        </Section>
        </>}

      </div>

      {sheetOpen && (
        <CopySheet
          platform={platform}
          formatted={formatted}
          source={{
            title: item.title,
            brand: item.brand,
            era: item.era,
            origin: item.origin,
            category: item.category,
            material: item.material,
            size: item.size,
            color: item.color,
            condition: item.condition,
            conditionNotes: item.conditionNotes,
            tags,
            price,
          }}
          copied={copied}
          opening={opening}
          writing={writing}
          aiActive={aiDescriptionActive}
          onCopy={copy}
          onOpen={openPlatform}
          onClose={() => setSheetOpen(false)}
          onRegenerate={() => generateDescriptions(true)}
        />
      )}
    </MobileFrame>
  );
}

/* ----------------------------- Copy Sheet ----------------------------- */

function CopySheet({
  platform,
  formatted,
  source,
  copied,
  opening,
  writing,
  aiActive,
  onCopy,
  onOpen,
  onClose,
  onRegenerate,
}: {
  platform: Platform;
  formatted: ReturnType<typeof formatForPlatform>;
  source: Parameters<typeof buildTxtFile>[1];
  copied: string | null;
  opening: boolean;
  writing: boolean;
  aiActive: boolean;
  onCopy: (k: string, t: string) => void;
  onOpen: () => void;
  onClose: () => void;
  onRegenerate: () => void;
}) {
  const link = platformLink(platform);

  const handleDownload = () => {
    const safeTitle = (source.title || "fig-listing").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
    const filename = `fig-${safeTitle}-${platform.toLowerCase()}.txt`;
    downloadTxt(filename, buildTxtFile(platform, source, formatted));
    toast.success("Listing saved to your device");
  };


  const Row = ({ label, value, hint }: { label: string; value: string; hint?: string }) => {
    const isCopied = copied === label;
    return (
      <div style={{ borderTop: "1px solid hsl(var(--ink))", padding: "14px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
          <span className="caps">{label}</span>
          <button
            onClick={() => onCopy(label, value)}
            style={{
              background: isCopied ? "hsl(var(--olive))" : "hsl(var(--ink))",
              color: "hsl(var(--cream))",
              border: "none",
              padding: "6px 12px",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {isCopied ? <>{Icon.check(12)} Copied</> : "Copy"}
          </button>
        </div>
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontSize: 14,
          color: "hsl(var(--ink))",
          lineHeight: 1.45,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}>
          {value}
        </div>
        {hint && (
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "hsl(var(--ink-2))", marginTop: 6, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {hint}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "hsl(0 0% 0% / 0.45)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 440,
          background: "hsl(var(--cream))",
          border: "1px solid hsl(var(--ink))",
          borderBottom: "none",
          maxHeight: "88vh",
          overflowY: "auto",
          padding: "18px 20px 28px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div className="caps" style={{ color: "hsl(var(--rust))" }}>Listing prepared</div>
            <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 26, letterSpacing: "-0.02em", lineHeight: 1.05 }}>
              Copy → paste into {platform}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "hsl(var(--ink))", padding: 4 }}>
            {Icon.x ? Icon.x(20) : <span style={{ fontSize: 20 }}>×</span>}
          </button>
        </div>

        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
          color: "hsl(var(--ink-2))", lineHeight: 1.4, marginBottom: 6,
        }}>
          Tap each <em>Copy</em>, then paste into the matching field on {platform}. Photos must be added from your camera roll on their side.
        </div>

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: 10, marginBottom: 2, gap: 8,
        }}>
          <span className="caps" style={{
            color: aiActive ? "hsl(var(--rust))" : "hsl(var(--ink-2))",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <span style={{ display: "inline-flex" }}>{Icon.sparkle(12)}</span>
            {writing
              ? "Writing for " + platform + "…"
              : aiActive
                ? "Tuned for " + platform + " by Fig"
                : "Default template"}
          </span>
          <button
            onClick={onRegenerate}
            disabled={writing}
            style={{
              background: "transparent", color: "hsl(var(--ink))",
              border: "1px solid hsl(var(--ink))",
              padding: "5px 10px",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
              letterSpacing: "0.08em", textTransform: "uppercase",
              cursor: writing ? "wait" : "pointer", opacity: writing ? 0.6 : 1,
            }}
          >
            {writing ? "Writing…" : aiActive ? "Rewrite" : "Generate"}
          </button>
        </div>

        <Row label="Title" value={formatted.title} hint={formatted.titleNote} />
        <Row label="Description" value={formatted.description} />
        <Row label="Tags" value={formatted.tags} hint={formatted.tagHelp} />
        <Row label="Price" value={`$${formatted.price}`} />

        <div style={{ borderTop: "1px solid hsl(var(--ink))", paddingTop: 14, marginTop: 4, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <button
            onClick={() => onCopy("Everything", formatted.all)}
            style={{
              background: copied === "Everything" ? "hsl(var(--olive))" : "transparent",
              color: copied === "Everything" ? "hsl(var(--cream))" : "hsl(var(--ink))",
              border: "1.5px solid hsl(var(--ink))",
              padding: "12px 8px",
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontWeight: 700, fontSize: 14,
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            {copied === "Everything" ? <>{Icon.check(14)} All copied</> : "Copy all as block"}
          </button>
          <button
            onClick={handleDownload}
            style={{
              background: "transparent",
              color: "hsl(var(--ink))",
              border: "1.5px solid hsl(var(--ink))",
              padding: "12px 8px",
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontWeight: 700, fontSize: 14,
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            ↓ Save as .txt
          </button>
        </div>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
          color: "hsl(var(--ink-2))", marginTop: 8,
          letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "center",
        }}>
          Save the .txt to keep this draft for later — re-open it any time
        </div>

        <button
          className="btn btn-rust"
          style={{ width: "100%", padding: "16px", fontSize: 15, marginTop: 14 }}
          onClick={onOpen}
          disabled={opening}
        >
          {opening ? "Opening…" : `${link.label} →`}
        </button>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
          color: "hsl(var(--ink-2))", textAlign: "center", marginTop: 8,
          letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          Opens {platform.toLowerCase()} in a new tab · marks this scan as listed
        </div>
      </div>
    </div>
  );
}
